import { Directive, HostBinding, Input, OnChanges, SimpleChanges } from '@angular/core';
import { timer } from 'rxjs';
import { take } from 'rxjs/operators';

/**
 * Directive that applies entrance animations to elements.
 * Controls fade-in direction and animation timing.
 *
 * @styles /ui/src/styles/ui/base/_helpers.scss
 * - .animated
 * - .fadeIn
 * - .fadeInTop
 * - .fadeInBottom
 * - .fadeInRight
 * - .fadeInLeft
 */
@Directive({
    selector: '[animated]',
    standalone: true,
})
export class AnimatedDirective implements OnChanges
{
    // direction to fade element in from
    @Input() fadeFrom: 'top' | 'bottom' | 'right' | 'left';

    // toggle animation for element
    @Input() hasAnimation = true;

    // allow element to animate for x milliseconds before toggling off (i.e. initial load/render of element)
    @Input() animateUntil: number;

    @HostBinding('class.animated') get animatedClass() {
        return this.hasAnimation;
    }
    @HostBinding('class.fadeIn') get fadeInClass() {
        return this.animatedClass && !this.fadeFrom;
    }
    @HostBinding('class.fadeInTop') get fadeInTopClass() {
        return this.animatedClass && this.fadeFrom === 'top';
    }
    @HostBinding('class.fadeInBottom') get fadeInBottomClass() {
        return this.animatedClass && this.fadeFrom === 'bottom';
    }
    @HostBinding('class.fadeInRight') get fadeInRightClass() {
        return this.animatedClass && this.fadeFrom === 'right';
    }
    @HostBinding('class.fadeInLeft') get fadeInLeftClass() {
        return this.animatedClass && this.fadeFrom === 'left';
    }

    ngOnChanges({ animateUntil }: SimpleChanges) {
        if (animateUntil?.currentValue) {
            timer(animateUntil.currentValue).pipe(take(1)).subscribe({
                next: () => this.hasAnimation = false,
            });
        }
    }
}

/**
 * Directive that staggers animation timing for sequential entrance effects.
 * Used with [animated] directive for coordinated animations.
 *
 * @styles /ui/src/styles/ui/base/_helpers.scss
 * - .animated-delay
 * - .animated-delay-list
 * - .animated-delay-table
 */
@Directive({
    selector: '[delayed]',
    standalone: true,
})
export class AnimatedDelayDirective
{
    @Input() set delayed (delayed: 'item' | 'list' | 'table' | 'card' | 'grid' | '') {
        this.type = delayed === '' ? 'item' : delayed;
    }

    private type: 'item' | 'list' | 'table' | 'card' | 'grid';

    @HostBinding('class.animated-delay') get delayedClass() {
        return this.type === 'item';
    }

    @HostBinding('class.animated-delay-list') get delayedLargeClass() {
        return this.type === 'list';
    }

    @HostBinding('class.animated-delay-table') get delayedTableClass() {
        return this.type === 'table';
    }

    @HostBinding('class.animated-delay-card') get delayedCardClass() {
        return this.type === 'card';
    }

    @HostBinding('class.animated-delay-grid') get delayedGridClass() {
        return this.type === 'grid';
    }
}
